import LoggingBase from "../../base/loggingbase"
import Utils from "../../utils/Utils";
import HtmHelper from "../../utils/HtmHelper";
import UIUtil from "../../gui/uiutil";

/** z-index used for the floating drag indicator line, well above everything else */
const GHOST_Z_INDEX = 100000000;

/** CSS filter applied to the drag indicator line to make it ~20% darker than the sash itself */
const GHOST_BRIGHTNESS = 'brightness(80%)';

/**
 * creates one of the two shared, initially hidden drag indicator line elements
 * (one for a vertical, one for a horizontal sash), appended once to the document
 * body and reused by all SplitSash instances. Its background color is set per-drag
 * (see {@link SplitSash#_showGhost}) to match whichever sash is currently dragged.
 * Carries its own centered grip icon (same as a real sash's), so it doesn't look
 * like the dots simply disappear while dragging.
 * @param {Boolean} vertical true for the vertical (col-resize) sash's indicator;
 * false for the horizontal (row-resize) sash's indicator
 * @returns {HTMLElement} the created indicator element
 */
function createGhostLine(vertical) {
	const line = document.createElement('div');
	line.id = vertical ? 'psawebcli-splitsash-ghost-vert' : 'psawebcli-splitsash-ghost-horz';
	line.style.position = 'absolute';
	line.style.zIndex = GHOST_Z_INDEX;
	line.style.filter = GHOST_BRIGHTNESS;
	line.style.display = 'none';
	const grip = document.createElement('div');
	grip.className = 'split-sash-grip' + (vertical ? ' split-sash-grip-vert' : '');
	line.appendChild(grip);
	document.body.appendChild(line);
	return line;
}

/** shared drag indicator line for vertical (col-resize) sashes */
const GHOST_VERT = createGhostLine(true);
/** shared drag indicator line for horizontal (row-resize) sashes */
const GHOST_HORZ = createGhostLine(false);

/**
 * class SplitSash - client-side counterpart of the Java SplitSash custom widget:
 * the draggable sash (divider) between the two child composites of a SplitForm.
 * Owns its own small DOM structure (an invisible full-size hit area plus a
 * centered 3-dot grip icon) rather than mirroring RAP's own Sash DOM structure.
 * While dragging, a shared floating line (one of {@link GHOST_VERT}/
 * {@link GHOST_HORZ}) previews where the sash would land; the actual move is only
 * reported to the server once, on mouse-up.
 */
export default class SplitSash extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} properties initialization properties
	 */
    constructor(properties) {
        super('widgets.splitsash.SplitSash');
		this._ready = false;
        Utils.bindAll(this, [ "layout", "onReady", "onRender",
			"_onMouseDown", "_onMouseMove", "_onMouseUp", "_onMouseLeave" ]);
		this.parent = rap.getObject(properties.parent);
		this.wdgId = this.parent.getData("pisasales.CSTPRP.IDW");
		this.element = document.createElement('div');
		if ( this.wdgId ) {
			this.element.id = 'sps-' + this.wdgId;
		}
		else {
			this.error('Missing widget ID (pisasales.CSTPRP.IDW)!');
		}
		const cwd = this.parent.getData('pisasales.CSTPRP.CWD') || {};
		// vertical: this sash is a vertical line dividing side-by-side composites,
		// dragged horizontally (cursor "col-resize"); otherwise it is a horizontal
		// line dividing vertically stacked composites, dragged vertically ("row-resize")
		this.vertical = !!cwd.vertical;
		// firstFixed: true if the near-side (top/left) sibling is the fixed-size one,
		// so the reported position is measured from the near parent edge; false if the
		// far-side (bottom/right) sibling is the fixed one, measured from the far edge
		this.firstFixed = !!cwd.firstFixed;
		// the sash's own thickness in pixels, as set on the Java side
		this.size = cwd.size || 1;
		// the owning SplitForm's own widget id; resolved to its DOM element (via
		// Utils.getRapWidget) lazily in onRender(), once the client-side widget
		// registry is guaranteed to know about it
		this.parentIdw = cwd.parentIdw;
		this.splitFormElm = null;
		// the sash's background color, as a CSS color string resolved by setBclr();
		// also applied (darkened) to the drag indicator while dragging
		this.bclr = null;
		this.dragging = false;
		this.element.className = 'split-sash';
		this.element.style.position = 'absolute';
		this.element.style.cursor = this.vertical ? 'col-resize' : 'row-resize';
		this.grip = document.createElement('div');
		this.grip.className = 'split-sash-grip' + (this.vertical ? ' split-sash-grip-vert' : '');
		this.element.appendChild(this.grip);
        // add our element to the RAP parent
		this.parent.append(this.element);
        // add resize listener
		this.parent.addListener("Resize", this.layout);
		// activate "render" event
		rap.on("render", this.onRender);
    }

	/**
	 * @returns {Boolean} true if this instance is fully initialized and its DOM
	 * element has been created; false otherwise (e.g. before the first "render"
	 * event, or after {@link #doDestroy}).
	 */
	get ready() {
		return this._ready && (this.element instanceof HTMLElement);
	}

    /**
     * destructor method; delegates to the base class, which invokes
     * {@link #doDestroy} (see {@link Base#destroy}).
     */
    destroy() {
        super.destroy();
    }

    /**
     * releases this instance's own resources: cancels an in-progress drag (if any),
     * removes the mousedown listener and the DOM element, and clears the
     * parent/element/grip references, then calls the base class cleanup.
     * @override
     */
    doDestroy() {
		this._release();
		if ( this.element ) {
			this.element.removeEventListener('mousedown', this._onMouseDown, true);
		}
        HtmHelper.rmvDomElm(this.element);
        this.parent = null;
		this.splitFormElm = null;
        this.element = null;
		this.grip = null;
        this._ready = false;
        super.doDestroy();
    }

	/**
	 * marks this instance as fully initialized (ready to be laid out) and starts
	 * listening for the user starting to drag the sash.
	 */
	onReady() {
		this._ready = true;
		this.element.addEventListener('mousedown', this._onMouseDown, true);
	}

	/**
	 * called once the client-side RAP toolkit has rendered this widget's DOM
	 * element for the first time; completes initialization, resolves the owning
	 * SplitForm's DOM element (see {@link #parentIdw}) and performs the first
	 * layout. This is the earliest point the SplitForm is guaranteed to be known
	 * to the client-side widget registry.
	 */
	onRender() {
		if ( this.element && this.element.parentNode ) {
			rap.off("render", this.onRender);
			const rw = Utils.getRapWidget(this.parentIdw);
			this.splitFormElm = rw ? rw.element : null;
			if ( !this.splitFormElm ) {
				this.error('Could not resolve the SplitForm element (parentIdw=' + this.parentIdw + ')!');
			}
			this.onReady();
			this.layout();
		}
	}

	/**
	 * resizes this sash's (invisible) DOM element to cover its parent's full
	 * client area; the visible grip icon stays centered within it via CSS.
	 */
	layout() {
		if ( this.ready ) {
			const area = this.parent.getClientArea();
			this.element.style.left = '0px';
			this.element.style.top = '0px';
			this.element.style.width = area[2] + 'px';
			this.element.style.height = area[3] + 'px';
		}
	}

	/**
	 * sets the sash's background color; also used (darkened) for the drag
	 * indicator while dragging, see {@link #_showGhost}.
	 * @param {*} args the color descriptor pushed from the Java side, or null/undefined
	 */
	setBclr(args) {
		this.bclr = UIUtil.getInstance().getCssRgb(args || null);
		if ( this.element ) {
			this.element.style.backgroundColor = this.bclr || '';
		}
	}

	/**
	 * stops further propagation/default handling of a DOM event.
	 * @param {Event} evt the event
	 */
	_stopEvt(evt) {
		evt.stopPropagation();
		evt.preventDefault();
	}

	/**
	 * "mousedown" listener on {@link #element}: starts a drag gesture (left button
	 * only) by capturing the sash's own and the overall SplitForm's current screen
	 * geometry (via {@link #splitFormElm} - {@link #parent} is RAP's own rendering
	 * container for just this sash, sized to this sash's thin bounds, not the
	 * SplitForm) and the mouse's starting position, then shows the drag indicator
	 * at the current (unmoved) sash position.
	 * @param {MouseEvent} evt the event
	 */
	_onMouseDown(evt) {
		this._stopEvt(evt);
		if ( this.dragging || (evt.button !== 0) || !this.splitFormElm ) {
			return;
		}
		this._startSashRect = this.element.getBoundingClientRect();
		this._startParentRect = this.splitFormElm.getBoundingClientRect();
		this._startMouse = { x: evt.pageX, y: evt.pageY };
		this.dragging = true;
		const body = document.body;
		// force the resize cursor for the whole window while dragging - without this
		// the cursor reverts to the default pointer as soon as it leaves this.element
		this._savedCursor = body.style.cursor;
		body.style.cursor = this.element.style.cursor;
		body.addEventListener('mousemove', this._onMouseMove, true);
		body.addEventListener('mouseup', this._onMouseUp, true);
		body.addEventListener('mouseleave', this._onMouseLeave, true);
		this._showGhost(this._computeGhost(0, 0));
	}

	/**
	 * "mousemove" listener on the document body: while dragging, moves the drag
	 * indicator to the (clamped) prospective new sash position.
	 * @param {MouseEvent} evt the event
	 */
	_onMouseMove(evt) {
		this._stopEvt(evt);
		if ( this.dragging ) {
			this._showGhost(this._computeGhost(evt.pageX - this._startMouse.x, evt.pageY - this._startMouse.y));
		}
	}

	/**
	 * "mouseup" listener on the document body: ends the drag gesture, hides the
	 * drag indicator and reports the final sash position to the server.
	 * @param {MouseEvent} evt the event
	 */
	_onMouseUp(evt) {
		this._stopEvt(evt);
		if ( this.dragging ) {
			const ghost = this._computeGhost(evt.pageX - this._startMouse.x, evt.pageY - this._startMouse.y);
			this._release();
			this._nfySrv('nfySashPos', { pos: this._toPos(ghost) });
		}
	}

	/**
	 * "mouseleave" listener on the document body: if the mouse left the browser
	 * window entirely while dragging (as opposed to merely bubbling from a child
	 * element), cancels the drag without reporting anything to the server - the
	 * same way a plain mouse-up outside the window would be lost.
	 * @param {MouseEvent} evt the event
	 */
	_onMouseLeave(evt) {
		this._stopEvt(evt);
		if ( this.dragging && (evt.target === document.body) ) {
			this._release();
		}
	}

	/**
	 * computes where the sash would land for the given mouse offset from the
	 * drag's starting position, clamped so the sash stays fully within its
	 * parent's client area.
	 * @param {Number} dx horizontal mouse offset in pixels since the drag started
	 * @param {Number} dy vertical mouse offset in pixels since the drag started
	 * @returns {Object} a plain object capturing the clamped near-edge position
	 * (screen coordinates) plus the geometry needed by {@link #_showGhost} and
	 * {@link #_toPos}
	 */
	_computeGhost(dx, dy) {
		const vertical = this.vertical;
		const parentRect = this._startParentRect;
		const sashRect = this._startSashRect;
		const nearLimit = vertical ? parentRect.left : parentRect.top;
		const farLimit = (vertical ? parentRect.right : parentRect.bottom) - this.size;
		let near = (vertical ? sashRect.left + dx : sashRect.top + dy);
		near = Math.min(Math.max(near, nearLimit), farLimit);
		return { vertical: vertical, parentRect: parentRect, near: near };
	}

	/**
	 * positions and shows the drag indicator line matching the given geometry,
	 * colored like this sash (see {@link #bclr}), darkened via {@link GHOST_BRIGHTNESS}.
	 * @param {Object} ghost geometry as computed by {@link #_computeGhost}
	 */
	_showGhost(ghost) {
		const parentRect = ghost.parentRect;
		const line = ghost.vertical ? GHOST_VERT : GHOST_HORZ;
		line.style.backgroundColor = this.bclr || '';
		if ( ghost.vertical ) {
			line.style.left = ghost.near + 'px';
			line.style.top = parentRect.top + 'px';
			line.style.width = this.size + 'px';
			line.style.height = parentRect.height + 'px';
		}
		else {
			line.style.left = parentRect.left + 'px';
			line.style.top = ghost.near + 'px';
			line.style.width = parentRect.width + 'px';
			line.style.height = this.size + 'px';
		}
		line.style.display = '';
		this._ghostLine = line;
	}

	/**
	 * hides the drag indicator line, if currently shown.
	 */
	_hideGhost() {
		if ( this._ghostLine ) {
			this._ghostLine.style.display = 'none';
			this._ghostLine = null;
		}
	}

	/**
	 * translates the given (clamped) screen geometry into the absolute position
	 * value expected by the server (the fixed child composite's new size - see
	 * the Java-side {@code SplitForm.setSashPos(int)}), taking {@link #firstFixed}
	 * into account.
	 * @param {Object} ghost geometry as computed by {@link #_computeGhost}
	 * @returns {Number} the new size, in pixels, of the fixed child composite
	 */
	_toPos(ghost) {
		const parentRect = ghost.parentRect;
		if ( this.firstFixed ) {
			return Math.round(ghost.near - (ghost.vertical ? parentRect.left : parentRect.top));
		}
		const farParent = ghost.vertical ? parentRect.right : parentRect.bottom;
		return Math.round(farParent - (ghost.near + this.size));
	}

	/**
	 * ends an in-progress drag gesture, if any: hides the drag indicator, restores
	 * the window's cursor, removes the body-level mouse listeners and clears the
	 * captured starting geometry. Does not notify the server - callers that should
	 * (a regular mouse-up) do so themselves afterwards.
	 */
	_release() {
		if ( this.dragging ) {
			this.dragging = false;
			this._hideGhost();
			const body = document.body;
			body.style.cursor = this._savedCursor;
			this._savedCursor = null;
			body.removeEventListener('mousemove', this._onMouseMove, true);
			body.removeEventListener('mouseup', this._onMouseUp, true);
			body.removeEventListener('mouseleave', this._onMouseLeave, true);
			this._startSashRect = null;
			this._startParentRect = null;
			this._startMouse = null;
		}
	}

	/**
	 * sends a notification to the web server.
	 * @param {String} code notification code
	 * @param {Object} par notification parameters
	 */
	_nfySrv(code, par) {
		if ( this.ready ) {
			const param = {};
			param.cod = code;
			param.par = par;
			rap.getRemoteObject(this).notify('SPLITSASH_NFY', param);
		}
	}

	/** register custom widget type */
	static register() {
		console.debug('Registering custom widget SplitSash.');
		rap.registerTypeHandler("psawidget.SplitSash", {
			factory : function(properties) {
				return new SplitSash(properties);
			},
			destructor: "destroy",
			properties: [ 'bclr' ],
			events: [ "SPLITSASH_NFY" ]
		});
	}
}
